import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';
import { serviceSchema } from '../lib/structuredData';
import {
  Calendar, ShieldCheck, ClipboardCheck, Sparkles, FileCheck2,
  Gauge, Filter, Droplets, Thermometer, ArrowRight,
} from 'lucide-react';

/**
 * Service & Maintenance — planned, contract-led page.
 * Visual tone: clean, professional, compliance-focused (teal accents).
 */
const ServiceMaintenance = () => {
  const checklist = [
    { icon: <Filter className="w-6 h-6" />, title: 'Filter clean & replace', desc: 'Indoor coil filters cleaned or swapped to spec.' },
    { icon: <Gauge className="w-6 h-6" />, title: 'Refrigerant pressure check', desc: 'Suction & discharge readings logged against manufacturer spec.' },
    { icon: <Droplets className="w-6 h-6" />, title: 'Condensate drain test', desc: 'Full drain flow & trap inspection — prevents leaks.' },
    { icon: <ShieldCheck className="w-6 h-6" />, title: 'Electrical isolator check', desc: 'Cabling, terminations, RCD and isolator verified safe.' },
    { icon: <Sparkles className="w-6 h-6" />, title: 'Indoor & outdoor coil clean', desc: 'Restores heat-transfer efficiency, lowers running costs.' },
    { icon: <Thermometer className="w-6 h-6" />, title: 'Return & supply air ΔT', desc: 'Measured cooling drop / heating lift recorded for compliance.' },
    { icon: <FileCheck2 className="w-6 h-6" />, title: 'F-Gas leak check & log', desc: 'Statutory annual leak check signed and retained 5 years.' },
    { icon: <ClipboardCheck className="w-6 h-6" />, title: 'Digital sign-off PDF', desc: 'Branded, signed report emailed to you the same day.' },
  ];

  const plans = [
    {
      name: 'Domestic Care',
      tag: 'For homes',
      best: 'Single-unit or multi-split homes',
      features: [
        'Annual service visit',
        'F-Gas leak check & log',
        'Filter clean + coil clean',
        'Priority booking window',
        'Branded digital sign-off PDF',
      ],
      accent: 'border-teal-500/30 hover:border-teal-400',
      badge: 'bg-teal-500/10 text-teal-300 border-teal-500/30',
    },
    {
      name: 'Commercial Standard',
      tag: 'For SMEs',
      best: '1–10 units, single site',
      features: [
        '2 service visits per year',
        'Statutory F-Gas compliance pack',
        'Priority 48-hour repair response',
        'Refrigerant top-ups included*',
        'Engineer Service Sign-Off PDF per visit',
      ],
      accent: 'border-[#FF8C00]/40 hover:border-[#FF8C00]',
      badge: 'bg-[#FF8C00]/15 text-[#FF8C00] border-[#FF8C00]/30',
      highlight: true,
    },
    {
      name: 'Estate / Multi-Site',
      tag: 'For property managers',
      best: '10+ units across multiple sites',
      features: [
        'Tailored PPM schedule',
        'Centralised asset register & F-Gas log',
        'Quarterly compliance report',
        '24/7 priority emergency response',
        'Single account manager + SLA',
      ],
      accent: 'border-amber-500/30 hover:border-amber-400',
      badge: 'bg-amber-500/10 text-amber-300 border-amber-500/30',
    },
  ];

  return (
    <div className="min-h-screen">
      <SEO
        title="Air Conditioning Service & Maintenance Contracts — London"
        description="Planned air-conditioning maintenance and F-Gas compliance contracts across London. Annual servicing, statutory leak checks, digital sign-off PDFs. Domestic, commercial and estate-grade plans."
        path="/service"
        jsonLd={serviceSchema(
          'Air Conditioning Service & Maintenance',
          'Planned annual / bi-annual servicing, F-Gas leak checks, digital reports and ongoing service contracts across London.'
        )}
      />

      {/* Hero */}
      <section
        className="relative min-h-[60vh] flex items-center bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(rgba(10, 14, 20, 0.55), rgba(10, 14, 20, 0.70)), url('/images/hero-commercial.jpg')`,
          backgroundPosition: 'center',
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full text-center">
          <span className="inline-flex items-center gap-2 text-xs bg-teal-500/15 px-4 py-2 rounded-full text-teal-300 font-bold border border-teal-500/30 tracking-wider mb-6">
            <ShieldCheck className="w-3.5 h-3.5" /> PLANNED MAINTENANCE & F-GAS COMPLIANCE
          </span>
          <h1 className="text-5xl md:text-6xl font-extrabold leading-tight mb-6">
            AC <span className="text-teal-300">Service</span> & Maintenance
          </h1>
          <p className="text-gray-300 text-lg sm:text-xl leading-relaxed max-w-3xl mx-auto mb-8">
            Stay efficient, stay compliant, stay ahead of breakdowns. We service every brand — and every visit ends with a branded, signed PDF report you can hand to your auditor.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-3">
            <Link to="/contact" data-testid="maint-hero-book-cta">
              <Button className="w-full sm:w-auto bg-teal-500 hover:bg-teal-600 text-white font-bold px-8 py-6 rounded-md text-base">
                <Calendar className="mr-2" size={20} /> Book a Service Visit
              </Button>
            </Link>
            <Link to="/ai-estimator">
              <Button variant="outline" className="w-full sm:w-auto border-white/30 hover:bg-white/10 text-white font-bold px-8 py-6 rounded-md text-base">
                Get a Contract Quote
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* What's included */}
      <section className="py-20 bg-[#0A0E14]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <p className="text-teal-300 font-bold text-xs tracking-[3px] uppercase mb-3">EVERY SERVICE VISIT INCLUDES</p>
            <h2 className="text-4xl md:text-5xl font-bold">The complete maintenance checklist</h2>
            <p className="text-gray-400 mt-4 max-w-2xl mx-auto">
              Every checklist item is recorded, signed, photographed, and delivered in a vector-PDF report — REFCOM-compliant.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {checklist.map((c, idx) => (
              <div
                key={idx}
                className="bg-[#121820] border border-white/10 rounded-2xl p-5 hover:border-teal-500/40 hover:-translate-y-1 transition-all"
                data-testid={`maint-check-${idx}`}
              >
                <div className="w-11 h-11 rounded-lg bg-teal-500/10 border border-teal-500/20 grid place-items-center mb-4 text-teal-300">
                  {c.icon}
                </div>
                <h3 className="text-base font-bold mb-1.5">{c.title}</h3>
                <p className="text-gray-400 text-xs leading-relaxed">{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Plans */}
      <section className="py-20 bg-[#121820]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#FF8C00] font-bold text-xs tracking-[3px] uppercase mb-3">CHOOSE YOUR PLAN</p>
            <h2 className="text-4xl md:text-5xl font-bold">Service contracts that scale with you</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {plans.map((plan, idx) => (
              <div
                key={idx}
                className={`relative bg-[#0A0E14] border rounded-2xl p-7 transition-all ${plan.accent} ${plan.highlight ? 'lg:scale-[1.03] shadow-2xl shadow-[#FF8C00]/10' : ''}`}
                data-testid={`maint-plan-${idx}`}
              >
                {plan.highlight && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-[10px] uppercase tracking-widest bg-[#FF8C00] text-[#0A0E14] font-bold px-3 py-1 rounded-full">
                    Most Popular
                  </span>
                )}
                <span className={`inline-block text-[10px] uppercase tracking-widest font-bold px-2.5 py-1 rounded-full border mb-4 ${plan.badge}`}>
                  {plan.tag}
                </span>
                <h3 className="text-2xl font-bold mb-2">{plan.name}</h3>
                <p className="text-gray-400 text-sm mb-6">{plan.best}</p>
                <ul className="space-y-3 mb-8">
                  {plan.features.map((f, j) => (
                    <li key={j} className="flex items-start gap-2 text-sm">
                      <ShieldCheck size={16} className="text-teal-400 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-300">{f}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/contact">
                  <Button className="w-full bg-white/5 hover:bg-white/10 border border-white/15 text-white font-bold py-3 rounded-md">
                    Enquire about this plan
                  </Button>
                </Link>
              </div>
            ))}
          </div>
          <p className="text-center text-xs text-gray-500 mt-6">
            * Commercial Standard includes refrigerant top-ups up to 200g per unit per year.
          </p>
        </div>
      </section>

      {/* Why it matters */}
      <section className="py-20 bg-[#0A0E14]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-teal-300 font-bold text-xs tracking-[3px] uppercase mb-3">WHY REGULAR SERVICING MATTERS</p>
            <h2 className="text-4xl md:text-5xl font-bold">Compliance, cost & comfort</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-[#121820] border border-white/10 rounded-2xl p-6">
              <FileCheck2 className="w-9 h-9 text-teal-300 mb-3" />
              <h3 className="text-lg font-bold mb-2">UK F-Gas compliance</h3>
              <p className="text-gray-400 text-sm leading-relaxed">
                Statutory leak checks are legally required for refrigerant systems above 5t CO₂ equivalent. Skip them and you risk significant fines.
              </p>
            </div>
            <div className="bg-[#121820] border border-white/10 rounded-2xl p-6">
              <Gauge className="w-9 h-9 text-teal-300 mb-3" />
              <h3 className="text-lg font-bold mb-2">Lower running costs</h3>
              <p className="text-gray-400 text-sm leading-relaxed">
                A clean, well-charged AC unit can run up to 25 % more efficiently — meaningful savings on heavy commercial loads.
              </p>
            </div>
            <div className="bg-[#121820] border border-white/10 rounded-2xl p-6">
              <ShieldCheck className="w-9 h-9 text-teal-300 mb-3" />
              <h3 className="text-lg font-bold mb-2">No surprise breakdowns</h3>
              <p className="text-gray-400 text-sm leading-relaxed">
                We catch failing capacitors, low charge and blocked drains before they become a Friday-evening emergency call-out.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-teal-700 to-teal-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-4 text-white">Ready to lock in your servicing?</h2>
          <p className="text-white/90 text-lg mb-8">Tell us your system and we'll quote a maintenance plan within the hour.</p>
          <Link to="/contact" data-testid="maint-cta-bottom">
            <Button className="bg-white hover:bg-white/90 text-teal-700 font-bold px-10 py-6 rounded-md text-base">
              <Calendar className="mr-2" size={20} /> Book a Service Visit <ArrowRight className="ml-2" size={20} />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ServiceMaintenance;
